import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import IBaseState from '../../types/IBaseState';
import { AppState } from '../../reducers/root.reducer';
import { RequestServiceResponse } from '@tcc-online/request-service';
import ProjectTranslation from '../types/project/ProjectTranslation';
import Scale from '../types/project/360/Scale';
import UploadState from '../../types/UploadState';
import Admin360ScaleService from '../services/Admin360ScaleService';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'Admin360Scales';

const getTranslations = createNotifiedThunk(
    `${NAME}/getTranslations`,
    Admin360ScaleService.getTranslations,
    { error: 'fe.admin.error.load-translations' },
);
const addLanguage = createNotifiedThunk(
    `${NAME}/addLanguage`,
    Admin360ScaleService.addLanguage,
    { error: 'fe.admin.error.save-language' },
);
const removeLanguage = createNotifiedThunk(
    `${NAME}/removeLanguage`,
    Admin360ScaleService.removeLanguage,
    { error: 'fe.admin.error.remove-language' },
);
const saveTranslations = createNotifiedThunk(
    `${NAME}/saveTranslations`,
    Admin360ScaleService.saveTranslations,
    { error: 'fe.admin.error.save-translation' },
);
const saveTranslation = createNotifiedThunk(
    `${NAME}/saveTranslation`,
    Admin360ScaleService.saveTranslation,
    { error: 'fe.admin.error.save-translation' },
);
const getScales = createNotifiedThunk(
    `${NAME}/getScales`,
    Admin360ScaleService.getScales,
    { error: 'fe.admin.error.load-scales' },
);
const getScale = createNotifiedThunk(
    `${NAME}/getScale`,
    Admin360ScaleService.getScale,
    { error: 'fe.admin.error.load-scale' },
);
const createScale = createNotifiedThunk(
    `${NAME}/createScale`,
    Admin360ScaleService.createScale,
    { error: 'fe.admin.error.create-scale' },
);
const updateScale = createNotifiedThunk(
    `${NAME}/updateScale`,
    Admin360ScaleService.updateScale,
    { error: 'fe.admin.error.update-scale' },
);
const patchValues = createNotifiedThunk(
    `${NAME}/patchValues`,
    Admin360ScaleService.patchValues,
    { error: 'fe.admin.error.update-values' },
);

const autoTranslate = createNotifiedThunk(
    `${NAME}/autoTranslate`,
    Admin360ScaleService.autoTranslate,
    { error: 'fe.admin.error.auto-translate' },
);

export interface Admin360ScaleState {
    translations: IBaseState<{ [key: string]: ProjectTranslation[] } | null>;
    scales: IBaseState<Scale[]>;
    detail: UploadState<Scale | undefined>;
}

const initialState: Admin360ScaleState = {
    translations: {
        data: null,
        loading: false,
    },
    scales: {
        data: [],
        loading: false,
    },
    detail: {
        data: undefined,
        loading: false,
        uploading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getScales.pending,
            (state: Admin360ScaleState): void => {
                state.scales.loading = true;
            },
        );
        builder.addCase(
            getScales.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale[]>>,
            ): void => {
                state.scales.data = action.payload.data;
            },
        );

        builder.addCase(getScale.pending, (state: Admin360ScaleState): void => {
            state.detail.loading = true;
        });
        builder.addCase(
            getScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );
        builder.addCase(
            createScale.pending,
            (state: Admin360ScaleState): void => {
                state.scales.loading = true;
            },
        );
        builder.addCase(
            createScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.scales.data.push(action.payload.data);
            },
        );

        builder.addCase(
            updateScale.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                state.detail.data = action.payload.data;
            },
        );

        builder.addCase(
            patchValues.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<RequestServiceResponse<Scale>>,
            ): void => {
                if (state.detail.data) {
                    state.detail.data = action.payload.data;
                }
            },
        );

        builder.addCase(
            getTranslations.pending,
            (state: Admin360ScaleState): void => {
                state.translations.loading = true;
            },
        );
        builder.addCase(
            getTranslations.fulfilled,
            (
                state: Admin360ScaleState,
                action: PayloadAction<
                    RequestServiceResponse<ProjectTranslation[]>
                > & {
                    meta: { arg: { scaleId: number; locale: string } };
                },
            ): void => {
                if (!state.translations.data) {
                    state.translations.data = {};
                }
                const locale = action.meta.arg.locale;
                state.translations.data[locale] = action.payload.data;
            },
        );

        builder.addCase(
            addLanguage.fulfilled,
            (state: Admin360ScaleState, action) => {
                if (state.detail.data) {
                    state.detail.data.languages = action.payload.data.languages;
                }
            },
        );

        builder.addCase(
            removeLanguage.fulfilled,
            (state: Admin360ScaleState, action) => {
                if (state.detail.data) {
                    state.detail.data.languages = action.payload.data.languages;
                }
            },
        );

        builder.addCase(
            saveTranslation.fulfilled,
            (state: Admin360ScaleState, action): void => {
                const { locale, translation } = action.meta.arg;
                const localeTranslations = state.translations.data?.[locale];
                if (!localeTranslations) return;
                const index = localeTranslations.findIndex(
                    (t) => t.id === translation.id,
                );
                if (index >= 0) {
                    localeTranslations[index] = translation;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getTranslations.fulfilled, getTranslations.rejected),
            (state: Admin360ScaleState): void => {
                state.translations.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getScales.fulfilled,
                getScales.rejected,
                createScale.fulfilled,
                createScale.rejected,
            ),
            (state: Admin360ScaleState): void => {
                state.scales.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getScale.fulfilled, getScale.rejected),
            (state: Admin360ScaleState): void => {
                state.detail.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(updateScale.pending, patchValues.pending),
            (state: Admin360ScaleState): void => {
                state.detail.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                updateScale.fulfilled,
                updateScale.rejected,
                patchValues.fulfilled,
                patchValues.rejected,
            ),
            (state: Admin360ScaleState): void => {
                state.detail.uploading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360ScaleState =>
    state.admin['360'].scales;

const Admin360ScaleSlice = {
    reducer: slice.reducer,
    actions: {
        getScales,
        getScale,
        createScale,
        updateScale,
        getTranslations,
        addLanguage,
        removeLanguage,
        saveTranslations,
        saveTranslation,
        patchValues,
        autoTranslate,
    },
    state: {
        all: state,
        scales: createSelector(
            state,
            (state: Admin360ScaleState) => state.scales,
        ),
        detail: createSelector(
            state,
            (state: Admin360ScaleState) => state.detail,
        ),
        translations: createSelector(
            state,
            (state: Admin360ScaleState) => state.translations,
        ),
        translationsByLocale: createSelector(
            [state, (state: AppState, locale: string) => locale],
            (state: Admin360ScaleState, locale: string) =>
                state.translations?.data?.[locale],
        ),
    },
};

export default Admin360ScaleSlice;
